import React, { useEffect, useState } from 'react'
import qs from 'query-string'
import {
  BlockingLoader,
  Button,
  Container,
  HtmlContent,
  Link,
  ListContainer,
  TextBody,
  TextCaption,
  TextTitle,
  ToastContext,
  Tools
} from '@pmi/pmi-iqos-ds-react'
import {
  analytics,
  buildOrderAdditionalData,
  CommonTranslations,
  cookie,
  goToRoute,
  extractErrorMessage,
  htmlTranslation,
  LayoutContainer,
  parseTranslations,
  ProfileAddressForm,
  PublishedComponent,
  Routes,
  Sites,
  constants,
  canUseDOM
} from '@pmi/pmi-iqos-shared-react'
import { getInitialPropsMock } from '@iqosphere-common/utils'
import { IqosphereApiManager } from '@iqosphere-common/services'
import TermsPopup from './terms-popup'
import InvalidAddressPopup from './invalid-address-popup'
import AddressConfirmationTranslations from './address-confirmation.json'

const ORDER_STATUS = {
  EXPIRED: 'Y',
  UNCLAIMED: 'Q'
}

const t4UserRegStatus = '4'

const errorsMappings = {
  ord_01: 'tk_errorPrizeOrder01',
  ord_02: 'tk_errorPrizeOrder02',
  ord_03: 'tk_errorPrizeOrder03',
  ord_04: 'tk_errorPrizeOrder04',
  ord_05: 'tk_errorPrizeOrder05',
  ord_06: 'tk_errorPrizeOrder06',
  ord_07: 'tk_errorPrizeOrder07'
}

const AddressConfirmationComponent = ({
  translations,
  banner,
  chatUrl,
  prizeId,
  orderId,
  loyaltyName,
  loyaltySection,
  campaigns,
  line,
  ultimatePrizeClaimConfirmationUrl
}) => {
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [isClaimed, setIsClaimed] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)
  const [isExpired, setIsExpired] = useState(false)
  const [addressData, setAddressData] = useState({})
  const [originalAddressData, setOriginalAddressData] = useState({})
  const [isAddressValid, setIsAddressValid] = useState(true)
  const [loading, setLoading] = useState(true)
  const [fetchedAddress, setFetchedAddress] = useState(false)
  const [callredirectUrl, setCallredirectUrl] = useState(null)
  const [userPrizeId, setUserPrizeId] = useState(prizeId)
  const [zenrinErrors, setZenrinErrors] = useState(null)
  const [showTermsAndCond, setShowTermsAndCond] = useState(false)
  const [orderSubtype, setOrderSubtype] = useState('')
  const [showAddressPopup, setShowAddressPopup] = useState(false)
  const [ignoreAddressValidation, setIgnoreAddressValidation] = useState(false)
  const [orderPrizeId, setOrderPrizeId] = useState('')

  const api = new IqosphereApiManager(line) // Only if in real Line context

  // Fix to get any case sensitive url params
  const params = canUseDOM ? qs.parse(window.location.search) : {}
  Object.keys(params).forEach(k => {
    params[k.toLocaleLowerCase()] = params[k]
  })

  // params.userprizeid exists if Line DD flow
  const islineUserPrizeId = !!params.userprizeid
  const isPrizeFlow = islineUserPrizeId ? !!params.userprizeid : !!orderId

  const formFieldNames = isPrizeFlow
    ? {
        postalCode: 'zipCode',
        prefecture: 'state',
        city: 'city',
        street: 'address'
      }
    : {
        postalCode: 'PostalCode',
        prefecture: 'Area',
        city: 'AddressLine01',
        street: 'AddressLine02'
      }

  const popupToast = ToastContext.getContextActions().popupToast

  const initMainAddress = () => ({
    ...Object.values(formFieldNames).reduce((a, v) => ({ ...a, [v]: '' }), {})
  })

  useEffect(() => {
    const profileInfo = cookie.getTokenInfo()
    if (profileInfo && profileInfo.reg_status !== t4UserRegStatus) {
      goToRoute({
        mainPath: Routes.conversion,
        isCurrentUrl: true
      })
      return
    }

    async function loadData() {
      setLoading(true)
      try {
        let mainAddress = {}
        if (isPrizeFlow) {
          const orderPrizeId = islineUserPrizeId ? params.userprizeid : orderId
          const res = await api.iqospherePrize.getOrderPrize(
            orderPrizeId,
            islineUserPrizeId
          )
          if (res.orderStatus === ORDER_STATUS.UNCLAIMED) {
            mainAddress = res.address || initMainAddress()
            setUserPrizeId(res.userPrizeId)
          } else if (res.orderStatus === ORDER_STATUS.EXPIRED) {
            setIsExpired(true)
            setIsConfirmed(true)
          } else {
            setIsConfirmed(true)
            setIsClaimed(true)
          }
          setOrderSubtype(res.orderSubtype)
          setOrderPrizeId(res.orderId)
        } else {
          const res = await api.iqosphereProfile.getProfile()
          mainAddress = res.Addresses.length
            ? res.Addresses.filter(key => key.Type === 1)[0]
            : {
                ...initMainAddress(),
                Type: 1,
                Country: 'JP'
              }
        }
        setIsCompleted(!Object.values(mainAddress).includes(''))
        setAddressData(mainAddress)
        setOriginalAddressData(mainAddress)
        saveCallredirectUrl()
      } catch (ex) {
        handleError(ex)
      } finally {
        setLoading(false)
        setFetchedAddress(true)
      }
    }
    loadData()
  }, [])

  const saveCallredirectUrl = () => {
    const params = qs.parse(window.location.search)
    if (
      params &&
      params.callredirectUrl &&
      callredirectUrl !== params.callredirectUrl + (window.location.hash || '')
    ) {
      setCallredirectUrl(
        params &&
          params.callredirectUrl &&
          params.callredirectUrl + (window.location.hash || '')
      )
    }
  }

  const checkMatchLevel = requireConfirmation => {
    if (requireConfirmation) {
      setIgnoreAddressValidation(true)
      setShowAddressPopup(true)
      return false
    }
    return true
  }

  const handleError = err => {
    if (
      isPrizeFlow &&
      err.data &&
      err.data.status &&
      err.data.status === 'rejected' &&
      err.data.rejectedFields &&
      err.data.rejectedFields.length
    ) {
      setIsCompleted(false)
      const newZenrinErrors = err.data.rejectedFields.reduce(
        (erros, field) => ({ ...erros, [field]: addressData[field] }),
        {}
      )
      if (err.data.rejectedFields.includes('address')) {
        popupToast({
          type: ToastContext.types.error,
          subtitle: translations.tk_zenrinRejectedAddress
        })
      }
      setZenrinErrors(newZenrinErrors)
      checkMatchLevel(err.data.requireConfirmation)
    } else {
      const errorCode = err && err.data && err.data[0] && err.data[0].code
      const toastTitle =
        errorCode && translations[errorsMappings[errorCode]]
          ? translations[errorsMappings[errorCode]]
          : extractErrorMessage(err, translations.tk_error)

      popupToast({
        type: ToastContext.types.error,
        subtitle: toastTitle
      })
    }
  }

  const goBack = () => {
    analytics.clickEvent({
      eventCategory: `Win ${loyaltySection}`,
      eventLabel: `${loyaltyName} - Back to previous`
    })
    goToRoute({
      mainPath:
        callredirectUrl ||
        (orderSubtype === constants.ORDER_SUBTYPES.TXU
          ? ultimatePrizeClaimConfirmationUrl
          : Routes.main),
      site: Sites.iqosphere
    })
  }

  const closeLiffPage = () => {
    canUseDOM && window.liff && window.liff.closeWindow()
  }

  const editAddress = () => {
    if (orderSubtype === constants.ORDER_SUBTYPES.TXS) {
      analytics.clickEvent({
        eventCategory: 'Address Confirmation',
        eventLabel: 'Edit Address',
        user_id: cookie.getSpiceId() || ''
      })
    }

    setIsCompleted(false)
  }

  const cancelEditAddress = () => {
    setIsCompleted(true)
    setAddressData(originalAddressData)
    setZenrinErrors(null)
  }

  const confirmAddress = async () => {
    setLoading(true)
    setShowTermsAndCond(false)
    onCloseInvalidAddressPopup()

    if (orderSubtype === constants.ORDER_SUBTYPES.TXS) {
      analytics.clickEvent({
        eventCategory: 'Address Confirmation',
        eventLabel: 'Proceed to Thank you page',
        user_id: cookie.getSpiceId() || ''
      })
    } else {
      analytics.clickEvent({
        eventCategory: `Win ${loyaltySection}`,
        eventLabel: `${loyaltyName} - Confirm to submit`
      })
    }
    try {
      if (isPrizeFlow) {
        const additionalData = buildOrderAdditionalData(true)
        const newData = {
          orderId: orderPrizeId,
          userPrizeId: userPrizeId,
          address: addressData,
          orderSubtype: orderSubtype || '',
          ...additionalData
        }
        const res = await api.iqospherePrize.confirmAddress(
          newData,
          ignoreAddressValidation || orderSubtype === 'PDV'
        )
        const confirmed = checkMatchLevel(res.requireConfirmation)
        confirmed && setIsConfirmed(true)
      } else {
        const newData = {
          userPrizeId: userPrizeId,
          addresses: [addressData]
        }
        const res = await api.iqosphereLottery.confirmAddress(newData)
        setIsConfirmed(res.success)
      }
    } catch (ex) {
      handleError(ex)
    } finally {
      setLoading(false)
    }
  }

  const onClickConfirmAddress = () => {
    if (orderSubtype === constants.ORDER_SUBTYPES.TXS) {
      setShowTermsAndCond(true)
      analytics.clickEvent({
        eventCategory: 'Address Confirmation',
        eventLabel: 'Update Success',
        user_id: cookie.getSpiceId() || ''
      })
    } else {
      confirmAddress()
    }
  }

  const handleChangeInput = e => {
    const newValue = e.target.value
    const name = e.target.name
    const newAddressData = { ...addressData, [name]: newValue }
    setAddressData(newAddressData)
  }

  const handleChangeZipCode = selectedOption => {
    if (selectedOption && selectedOption.item) {
      setAddressData({
        ...addressData,
        [formFieldNames.city]:
          selectedOption.item.city + ' ' + selectedOption.item.village,
        [formFieldNames.postalCode]: selectedOption.value,
        [formFieldNames.prefecture]: selectedOption.item.state
      })
    }
  }

  const handleFormIsValid = isValid => {
    setIsAddressValid(isValid)
  }

  const onCloseInvalidAddressPopup = () => {
    setShowAddressPopup(false)
    setIgnoreAddressValidation(false)
  }

  return (
    <Container fullWidth position={Container.positions.relative} py="8">
      {loading ? (
        <BlockingLoader loading />
      ) : (
        <ListContainer
          xBetween="24"
          yBetween="16"
          fullWidth
          columns={[1, 1, 2]}>
          <Container fullWidth>
            <HtmlContent fullWidth content={banner} />
          </Container>
          <Container fullWidth>
            <LayoutContainer fullWidth xMainLayout>
              <Container
                fullWidth
                display={Container.displays.flex}
                flexDirection={Container.flexDirections.column}
                alignItems={[
                  Container.alignItemsValues.center,
                  Container.alignItemsValues.center,
                  Container.alignItemsValues.start
                ]}
                pb="12"
                divider={Container.dividers.bottom}>
                <TextTitle
                  heading={TextTitle.headings.h3}
                  alignment={
                    Tools.isDesktop()
                      ? TextTitle.alignments.left
                      : TextTitle.alignments.center
                  }>
                  {isExpired
                    ? orderSubtype === constants.ORDER_SUBTYPES.TXU
                      ? translations.tk_titleExpiredTXU
                      : translations.tk_titleOrderExpired
                    : isConfirmed &&
                      orderSubtype === constants.ORDER_SUBTYPES.TXS
                    ? translations.tk_titleAddressConfirmedTXWave4
                    : isClaimed && orderSubtype === constants.ORDER_SUBTYPES.TXU
                    ? translations.tk_titleClaimedTXU
                    : isConfirmed
                    ? translations.tk_titleAddressConfirmed
                    : translations.tk_titleAddressNotConfirmed}
                </TextTitle>
                <Container pt="12">
                  <TextBody
                    alignment={
                      Tools.isDesktop()
                        ? TextBody.alignments.left
                        : TextBody.alignments.center
                    }
                    {...htmlTranslation(
                      translations,
                      isExpired
                        ? orderSubtype === constants.ORDER_SUBTYPES.TXU
                          ? 'tk_descriptionExpiredTXU'
                          : 'tk_titleOrderExpiredDescription'
                        : isConfirmed &&
                          orderSubtype === constants.ORDER_SUBTYPES.TXS
                        ? 'tk_formDescriptionConfirmedTXWave4'
                        : isClaimed &&
                          orderSubtype === constants.ORDER_SUBTYPES.TXU
                        ? 'tk_descriptionClaimedTXU'
                        : isConfirmed
                        ? 'tk_formDescriptionConfirmed'
                        : 'tk_formDescriptionNotConfirmed'
                    )}
                  />
                </Container>
              </Container>
              {isConfirmed ? (
                <Container
                  display={Container.displays.flex}
                  flexDirection={Container.flexDirections.column}
                  alignItems={[
                    Container.alignItemsValues.center,
                    Container.alignItemsValues.center,
                    Container.alignItemsValues.start
                  ]}>
                  {translations.tk_clientCenter.trim() && (
                    <Container pt="8">
                      <TextTitle
                        heading={TextTitle.headings.h5}
                        alignment={
                          Tools.isDesktop()
                            ? TextTitle.alignments.left
                            : TextTitle.alignments.center
                        }>
                        {translations.tk_clientCenter}
                      </TextTitle>
                    </Container>
                  )}
                  {translations.tk_clientCenterLinkText.trim() && (
                    <Container
                      fullWidth
                      py="8"
                      divider={Container.dividers.bottom}
                      display={Container.displays.flex}
                      justifyContent={[
                        Container.justifyContentValues.center,
                        Container.justifyContentValues.center,
                        Container.justifyContentValues.start
                      ]}>
                      <Link href={chatUrl}>
                        {translations.tk_clientCenterLinkText}
                      </Link>
                    </Container>
                  )}
                  <Container pt="12" fullWidth={[true, false]}>
                    <Button
                      fullWidth
                      variant={Button.variants.secondary}
                      text={
                        line
                          ? translations.tk_closeLiffPage
                          : translations.tk_back
                      }
                      onClickHandler={line ? closeLiffPage : goBack}
                    />
                  </Container>
                </Container>
              ) : (
                <Container pt="8">
                  <TextCaption>{translations.tk_registeredAddress}</TextCaption>
                  <Container pt="2">
                    {fetchedAddress && (
                      <>
                        <ProfileAddressForm
                          translations={translations}
                          formFieldNames={formFieldNames}
                          updatedFormData={addressData}
                          handleChangeInput={handleChangeInput}
                          handleChangeZipCode={handleChangeZipCode}
                          handleFormIsValid={handleFormIsValid}
                          toggleEdit={!isCompleted}
                          isSavingData={false}
                          site={Sites.iqosphere}
                          zenrinErrors={zenrinErrors}
                          expandedAddressFields
                        />
                        <Container pb="8" divider={Container.dividers.bottom}>
                          {isCompleted ? (
                            <Container pt="8">
                              <Button
                                variant={Button.variants.secondary}
                                text={translations.tk_updateAddress}
                                onClickHandler={editAddress}
                              />
                            </Container>
                          ) : (
                            <Container pt="4">
                              <TextBody
                                alignment={
                                  Tools.isDesktop()
                                    ? TextBody.alignments.left
                                    : TextBody.alignments.center
                                }
                                {...htmlTranslation(
                                  translations,
                                  'tk_addressFormDisclaimer'
                                )}
                              />
                              <Container pt="8">
                                <Button
                                  variant={Button.variants.secondary}
                                  text={translations.tk_updateAddressCancel}
                                  onClickHandler={cancelEditAddress}
                                />
                              </Container>
                            </Container>
                          )}
                        </Container>
                      </>
                    )}
                    <Container
                      pt="8"
                      fullWidth
                      display={Container.displays.flex}
                      justifyContent={[
                        Container.justifyContentValues.center,
                        Container.justifyContentValues.center,
                        Container.justifyContentValues.start
                      ]}>
                      <Button
                        disabled={!isAddressValid}
                        text={translations.tk_confirmAddress}
                        onClickHandler={onClickConfirmAddress}
                      />
                    </Container>
                  </Container>
                </Container>
              )}
            </LayoutContainer>
          </Container>
        </ListContainer>
      )}
      <TermsPopup
        isOpen={showTermsAndCond}
        onClose={() => setShowTermsAndCond(false)}
        onAccept={confirmAddress}
        content={campaigns?.togetherx?.tandc}
        translations={translations}
      />
      <InvalidAddressPopup
        isOpen={showAddressPopup}
        onClose={onCloseInvalidAddressPopup}
        onAccept={confirmAddress}
        loading={loading}
        translations={translations}
      />
    </Container>
  )
}

const AddressConfirmation = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={AddressConfirmationComponent}
        {...ComponentDefaultProps}
        {...props}
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...AddressConfirmationTranslations,
  ...CommonTranslations
}

const ComponentDefaultProps = {
  componentName: 'AddressConfirmation',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  }
}

AddressConfirmation.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    line: !!req?.query?.line,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: async () => {
      const banner =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('addressConfirmationBanner')
          : (req.body.extra && req.body.extra.addressConfirmationBanner) || ''
      const chatUrl =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('chatUrl')
          : (req.body.extra && req.body.extra.iqosChatUrl) || ''
      const prizeId =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('prizeId')
          : (req.body.extra && req.body.extra.prizeId) || ''
      const orderId =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('orderId')
          : (req.body.extra && req.body.extra.orderId) || ''
      const loyaltyName =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('loyaltyName')
          : (req.body.extra && req.body.extra.loyaltyName) || ''
      const loyaltySection =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('loyaltySection')
          : (req.body.extra && req.body.extra.loyaltySection) || ''
      const campaigns =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('campaignsTogetherX')
          : (req.body.extra && req.body.extra.campaigns) || null
      const ultimatePrizeClaimConfirmationUrl =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('ultimatePrizeClaimConfirmationUrl')
          : (req.body.extra &&
              req.body.extra.ultimatePrizeClaimConfirmationUrl) ||
            ''

      return {
        banner,
        chatUrl,
        prizeId,
        orderId,
        loyaltyName,
        loyaltySection,
        campaigns,
        ultimatePrizeClaimConfirmationUrl
      }
    }
  })
}

export default AddressConfirmation
